import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';

type FormValues = {
  tenantSlug: string;
  email: string;
  password: string;
  totpCode?: string;
};

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const tenantSlug = useAuthStore((s) => s.tenantSlug);
  const [error, setError] = useState<string | null>(null);
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      tenantSlug: tenantSlug || 'demo',
      email: 'admin@demo.parking',
      password: 'Admin123!',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    setLoading(true);
    try {
      if (preAuthToken) {
        if (!values.totpCode || values.totpCode.length !== 6) {
          setError(t('login.totpHint'));
          return;
        }
        const { data } = await api.verifyTotp({
          preAuthToken,
          code: values.totpCode,
        });
        if (data.status !== 'authenticated') {
          setError(t('common.loginFailed'));
          return;
        }
        setSession({
          accessToken: data.tokens.accessToken,
          refreshToken: data.tokens.refreshToken,
          user: data.user,
          tenantSlug: values.tenantSlug,
          expiresIn: data.tokens.expiresIn,
        });
        navigate('/dashboard');
        return;
      }

      const payload: {
        tenantSlug: string;
        email: string;
        password: string;
        totpCode?: string;
      } = {
        tenantSlug: values.tenantSlug,
        email: values.email,
        password: values.password,
      };
      if (values.totpCode && values.totpCode.length === 6) {
        payload.totpCode = values.totpCode;
      }

      const { data } = await api.login(payload);

      if (data.status === 'totp_required') {
        setPreAuthToken(data.preAuthToken);
        return;
      }

      setSession({
        accessToken: data.tokens.accessToken,
        refreshToken: data.tokens.refreshToken,
        user: data.user,
        tenantSlug: values.tenantSlug,
        expiresIn: data.tokens.expiresIn,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.loginFailed'));
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="symbol symbol-primary">
            <i className="bi bi-p-square-fill" />
          </span>
          <span className="fw-bold">{t('brand')}</span>
        </div>
        <h1>{t('login.title')}</h1>
        <p className="lead">{t('login.subtitle')}</p>
        <form onSubmit={onSubmit} className="d-grid gap-3">
          {error ? <div className="alert alert-danger mb-0">{error}</div> : null}
          {preAuthToken ? <div className="alert alert-info mb-0">{t('login.totpHint')}</div> : null}
          {!preAuthToken && (
            <>
              <div>
                <label className="form-label">{t('login.tenant')}</label>
                <input className="form-control" {...register('tenantSlug', { required: true })} />
              </div>
              <div>
                <label className="form-label">{t('login.email')}</label>
                <input
                  className="form-control"
                  type="email"
                  {...register('email', { required: true })}
                />
              </div>
              <div>
                <label className="form-label">{t('login.password')}</label>
                <input
                  className="form-control"
                  type="password"
                  {...register('password', { required: true })}
                />
              </div>
            </>
          )}
          {preAuthToken && (
            <div>
              <label className="form-label">{t('login.totp')}</label>
              <input
                className="form-control"
                autoFocus
                inputMode="numeric"
                maxLength={6}
                {...register('totpCode', { required: true })}
              />
            </div>
          )}
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
            {loading ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              t('login.cta')
            )}
          </button>
          <RouterLink to="/platform/login" className="btn btn-light">
            {t('platform.login.submit')} (Platform)
          </RouterLink>
        </form>
      </div>
    </div>
  );
}
